/**
 * PressBoxAdminTab — 2026-10-22 (per Gilles). The super admin's Press Box
 * console, in one tab:
 *   · the live queue (same card as the Stats AI tab)
 *   · every client → their teams → their games (what they see), with
 *     bulk-select → Re-run (AI pass, or a fresh transcription)
 *   · the narrator's activity trail per team (uploads, passes, publishes)
 *   · a Debug JSON per game (the existing /admin/recordings/:id/debug-json)
 *   · the Meta WhatsApp Cloud API integration: settings in the app (no
 *     redeploy), Test connection, Subscribe webhook, delivery stats —
 *     straight from the DoctaDirect handoff guide.
 */
import { useEffect, useState } from 'react';
import { RefreshCw, FileText, Activity, ExternalLink, Copy, MessageCircle, CheckCircle2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { PressBoxQueueCard } from './StatsAiTab.jsx';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

export default function PressBoxAdminTab({ T }) {
  // 2026-10-23 (per Gilles) — the AI Lab: models picked here are used by the
  // console's "Compare models on N selected" button.
  const [labModels, setLabModels] = useState(['gemma4:31b', 'qwen3:32b']);
  const [labTick, setLabTick] = useState(0);
  const compare = async (items) => {
    if (!labModels.length) { toast.error('Pick at least one model in the AI Lab card'); return; }
    if (!window.confirm(`Compare ${labModels.join(' vs ')} on ${items.length} game(s)?\n\nShadow runs: each model re-reads the kept transcript; the games' own stats are NOT changed. One run at a time, never while a real pass is running (~15-30 min per model per game on the 5090).`)) return;
    try {
      const { data } = await api.post('/admin/press-box/lab/compare', { items, models: labModels });
      toast.success(`${data.queued} comparison run(s) queued${data.skipped?.length ? ` — ${data.skipped.length} game(s) skipped (no finished recording)` : ''}`);
      setLabTick(x => x + 1);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not start the comparison'); }
  };
  return (
    <div>
      <GpuPolicyCard T={T} />
      <PressBoxQueueCard T={T} />
      {/* 2026-10-27 (per Gilles) — the Pass log right under the queue, easy
          to find: every pass, the re-run buttons, the comparisons. */}
      <PassLogCard T={T} labModels={labModels} />
      <GameVideosCard T={T} />
      <ConsoleCard T={T} onCompare={compare} />
      <AiLabCard T={T} models={labModels} setModels={setLabModels} tick={labTick} />
      <VideoAnalysisCard T={T} />
      <WhatsAppCard T={T} />
    </div>
  );
}

/* ── 2026-11-03 (per Gilles: "run everything on the 5090, let me toggle the
      1070 on") — ONE GPU policy for every step that needs a card ─────────── */
function GpuPolicyCard({ T }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 16 };
  const load = async () => {
    try { const { data } = await api.get('/admin/stats-ai'); setS({ policy: data.settings?.gpuPolicy || '5090_only', plan: data.gpuPlan || [], workers: data.visionWorkers || [] }); }
    catch (e) { setS({ error: e.response?.data?.error || 'Could not load' }); }
  };
  useEffect(() => { load(); }, []);
  const save = async (policy) => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/stats-ai', { gpuPolicy: policy });
      toast.success('GPU policy saved — it applies to the next pass or run');
      setS(x => ({ ...x, policy: data.settings?.gpuPolicy || policy }));
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setSaving(false); }
  };
  const OPTIONS = [
    ['5090_only', '5090 only', 'every step waits for the 5090 (one GPU job at a time; models are swapped on the card as needed) — the 1070 is never used'],
    ['5090_first', '5090 first, 1070 when busy', 'speech-to-text and the tracker borrow the 1070 while the 5090 is taken'],
    ['both', '5090 and 1070 side by side', 'the two cards share the work; passes may overlap'],
  ];
  return (
    <div style={card} data-gpu-policy>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>GPUs — which card does what</h3>
        <span style={{ fontSize: 12, color: T.textDim }}>one policy for speech-to-text, the stats model and the video tracker</span>
      </div>
      {!s && <div style={{ color: T.textDim, fontSize: 13 }}>Loading…</div>}
      {s?.error && <div style={{ color: '#ff6b6b', fontSize: 13 }}>{s.error}</div>}
      {s && !s.error && (<>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {OPTIONS.map(([k, label, hint]) => (
            <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: T.text, cursor: 'pointer' }}>
              <input type="radio" name="gpu_policy" value={k} checked={s.policy === k} disabled={saving} onChange={() => save(k)} data-gpu-policy-option={k} style={{ marginTop: 3 }} />
              <span><strong>{label}</strong> <span style={{ color: T.textDim }}>— {hint}</span></span>
            </label>
          ))}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }} data-gpu-plan>
          <thead><tr style={{ color: T.textDim, textAlign: 'left' }}><th style={{ padding: '2px 6px' }}>step</th><th style={{ padding: '2px 6px' }}>card under this policy</th></tr></thead>
          <tbody>{s.plan.map((row, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${T.border}` }}><td style={{ padding: '3px 6px', fontWeight: 600, color: T.text }}>{row.step}</td><td style={{ padding: '3px 6px', color: T.textDim, whiteSpace: 'normal' }}>{row.gpu}</td></tr>
          ))}</tbody>
        </table>
        <div style={{ fontSize: 12, color: T.textDim, marginTop: 8 }}>
          Vision workers this server knows (from its .env): {s.workers.length ? s.workers.map(w => `${w.name} at ${w.host}`).join(' · ') : 'none set up (VISION_WORKER_URL)'}.
          {s.workers.length === 1 && s.policy === '5090_only' ? ' Only one worker is set up, so the tracker runs there whatever the policy says — add the 5090 worker (VISION_WORKER_URL) and the 1070 (VISION_WORKER_FALLBACK_URL) to /srv/pucksync/.env to split them.' : ''}
        </div>
      </>)}
    </div>
  );
}

/* ── clients → teams → games, bulk re-run, activity ─────────────────────── */
function ConsoleCard({ T, onCompare = null }) {
  const [teams, setTeams] = useState(null);
  const [open, setOpen] = useState(null);         // teamId expanded
  const [games, setGames] = useState({});         // teamId -> games[]
  const [sel, setSel] = useState({});             // `${teamId}:${gameId}` -> true
  const [act, setAct] = useState(null);           // { teamId, rows }
  const [busy, setBusy] = useState(false);
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 16 };

  const load = async () => {
    try { const { data } = await api.get('/admin/press-box/teams'); setTeams(data.teams); }
    catch (e) { setTeams({ error: e.response?.data?.error || 'Could not load' }); }
  };
  useEffect(() => { load(); }, []);

  const toggleTeam = async (t) => {
    if (open === t.id) { setOpen(null); return; }
    setOpen(t.id); setAct(null);
    if (!games[t.id]) {
      try { const { data } = await api.get(`/admin/press-box/teams/${t.id}/games`, { params: { season: 'all' } });
        setGames(g => ({ ...g, [t.id]: data.games || [] })); }
      catch { setGames(g => ({ ...g, [t.id]: [] })); }
    }
  };
  const picked = Object.keys(sel).filter(k => sel[k]);
  // 2026-11-03 (per Gilles) — bulk re-run of the AUDIO pass, the VIDEO AI or
  // both, on the selected games or all of a team's games; each joins the one
  // line for the 5090 and starts by itself; a game with no audio / no video
  // is logged as skipped in the Pass log.
  const rerun = async (what, mode = 'ai') => {
    const items = picked.map(k => { const [teamId, gameId] = k.split(':'); return { teamId, gameId }; });
    if (!items.length) return;
    const label = what === 'video' ? 'Run the video AI on' : what === 'both' ? `${mode === 'fresh' ? 'Transcribe + analyse the audio AND run the video AI' : 'Run Press Box AI AND the video AI'} on` : (mode === 'fresh' ? 'Transcribe AND analyse' : 'Run Press Box AI on');
    if (!window.confirm(`${label} ${items.length} game(s)?\n\nThey join ONE line for the 5090 and run one at a time, first come first served. A game with no audio / no video is skipped and logged.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/admin/press-box/rerun', { items, mode, what });
      const skips = data.results.flatMap(r => [r.audio && !r.audio.ok ? `audio: ${r.audio.why}` : null, r.video && !r.video.ok ? `video: ${r.video.why}` : null].filter(Boolean));
      toast.success(`${data.started} game(s) in line${skips.length ? ` — ${skips.length} skipped (${[...new Set(skips)].slice(0, 3).join('; ')})` : ''}`, { duration: 8000 });
      setSel({});
    } catch (e) { toast.error(e.response?.data?.error || 'Could not start them'); }
    finally { setBusy(false); }
  };
  const selectAll = (t, on) => {
    const next = { ...sel };
    for (const g of (games[t.id] || [])) { const k = `${t.id}:${g.id}`; if (g.recording?.id || g.video) next[k] = on; else delete next[k]; }
    setSel(next);
  };
  const showActivity = async (teamId) => {
    setAct({ teamId, rows: null });
    try { const { data } = await api.get(`/admin/press-box/teams/${teamId}/activity`); setAct({ teamId, rows: data.rows }); }
    catch { setAct({ teamId, rows: [] }); }
  };

  return (
    <div style={card} data-pb-console>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Press Box — clients, teams and games</h3>
        <button type="button" onClick={load} style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}><RefreshCw size={12} /> Refresh</button>
      </div>
      {!teams && <div style={{ color: T.textDim, fontSize: 13 }}>Loading…</div>}
      {teams?.error && <div style={{ color: '#ff6b6b', fontSize: 13 }}>{teams.error}</div>}
      {Array.isArray(teams) && teams.map(t => (
        <div key={t.id} style={{ border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
          <button type="button" onClick={() => toggleTeam(t)} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'transparent', border: 'none', cursor: 'pointer', color: T.text, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700 }}>{t.name}</span>
            <span style={{ fontSize: 12, color: T.textDim }}>{t.owner || ''} {t.ownerEmail ? `· ${t.ownerEmail}` : ''}</span>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: T.textDim }}>
              {t.games} games · {t.recordings} recordings · {t.publicEnabled ? 'page live' : 'page off'} · last activity {fmt(t.lastActivity) || '—'}
            </span>
          </button>
          {open === t.id && (
            <div style={{ borderTop: `1px solid ${T.border}`, padding: '8px 12px' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <a href={`/press-box/${t.id}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: T.ice, textDecoration: 'none' }}><ExternalLink size={12} /> Open as they see it</a>
                <button type="button" onClick={() => showActivity(t.id)} style={{ fontSize: 12, padding: '3px 9px', borderRadius: 8, cursor: 'pointer', background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}><Activity size={12} /> Activity trail</button>
                <label style={{ fontSize: 12, color: T.textDim, display: 'inline-flex', alignItems: 'center', gap: 4 }} data-pb-select-all>
                  <input type="checkbox" checked={(games[t.id] || []).some(g => g.recording?.id || g.video) && (games[t.id] || []).filter(g => g.recording?.id || g.video).every(g => sel[`${t.id}:${g.id}`])}
                    onChange={(e) => selectAll(t, e.target.checked)} /> Select all
                </label>
                {picked.length > 0 && <>
                  <button type="button" disabled={busy} onClick={() => rerun('audio', 'ai')} data-pb-rerun="audio" style={{ fontSize: 12, padding: '3px 9px', borderRadius: 8, cursor: 'pointer', background: T.ice, border: 'none', color: '#fff', fontWeight: 700 }}>Audio: Press Box AI on {picked.length}</button>
                  <button type="button" disabled={busy} onClick={() => rerun('audio', 'fresh')} style={{ fontSize: 12, padding: '3px 9px', borderRadius: 8, cursor: 'pointer', background: 'none', border: `1px solid ${T.ice}`, color: T.ice, fontWeight: 700 }}>Audio: transcribe + AI on {picked.length}</button>
                  <button type="button" disabled={busy} onClick={() => rerun('video')} data-pb-rerun="video" style={{ fontSize: 12, padding: '3px 9px', borderRadius: 8, cursor: 'pointer', background: 'none', border: '1px solid #b069ff', color: '#b069ff', fontWeight: 700 }}>Video AI on {picked.length}</button>
                  <button type="button" disabled={busy} onClick={() => rerun('both')} style={{ fontSize: 12, padding: '3px 9px', borderRadius: 8, cursor: 'pointer', background: 'none', border: `1px solid ${T.border}`, color: T.text, fontWeight: 700 }}>Both on {picked.length}</button>
                  {onCompare && (
                    <button type="button" disabled={busy}
                      onClick={() => onCompare(picked.map(k => { const [teamId, gameId] = k.split(':'); return { teamId, gameId }; }))}
                      title="AI Lab: run the models picked in the AI Lab card on these games as shadow runs (the games' stats are not changed) and score each against the corrected stats"
                      style={{ fontSize: 12, padding: '3px 9px', borderRadius: 8, cursor: 'pointer', background: 'none', border: '1px solid #b07cff', color: '#b07cff', fontWeight: 700 }}>
                      Compare models on {picked.length}
                    </button>
                  )}
                </>}
              </div>
              {!games[t.id] && <div style={{ color: T.textDim, fontSize: 12 }}>Loading games…</div>}
              {(games[t.id] || []).map(g => {
                const k = `${t.id}:${g.id}`;
                const recId = g.recording?.id;
                return (
                  <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12, color: T.text, borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap' }}>
                    <input type="checkbox" checked={!!sel[k]} disabled={!recId && !g.video}
                      title={recId || g.video ? 'Select for bulk re-run' : 'No audio and no video — nothing to re-run'}
                      onChange={(e) => setSel(x => ({ ...x, [k]: e.target.checked }))} />
                    <span style={{ minWidth: 86, color: T.textDim }}>{g.date}</span>
                    <span style={{ fontWeight: 600 }}>vs {g.opponent || 'TBA'}</span>
                    <span style={{ color: recId ? 'var(--goal)' : T.textDim }} title={recId ? 'audio uploaded' : 'no audio'}>audio {recId ? '✓' : '—'}</span>
                    <span style={{ color: g.video ? '#b069ff' : T.textDim }} title={g.video ? `video ${g.video.status || ''}` : 'no video'}>video {g.video ? '✓' : '—'}</span>
                    <span style={{ color: T.textDim }}>{g.score ? `${g.score.us}–${g.score.them}` : '—'}</span>
                    <span style={{ color: T.textDim }}>{g.recording?.engine || 'no recording'}{g.recording?.aiHold ? ' · on hold' : g.recording?.aiQueued ? ' · queued' : ''}</span>
                    {g.needsReview > 0 && <span style={{ color: '#ff9500' }}>{g.needsReview} to check</span>}
                    {recId && (
                      <button type="button" style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 8, cursor: 'pointer', background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}
                        title="Everything about this game's pass in one JSON (transcript, analysis, events, roster, log)"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/admin/recordings/${recId}/debug-json`, { responseType: 'blob' });
                            const a = document.createElement('a');
                            a.href = URL.createObjectURL(res.data);
                            a.download = `pressbox_debug_${recId.slice(0, 8)}.json`;
                            a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 5000);
                          } catch { toast.error('Could not build the debug file'); }
                        }}>
                        <FileText size={11} /> Debug JSON
                      </button>
                    )}
                  </div>
                );
              })}
              {act?.teamId === t.id && (
                <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto', background: T.surface2 || 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 4 }}>Activity trail (newest first)</div>
                  {!act.rows && <div style={{ fontSize: 12, color: T.textDim }}>Loading…</div>}
                  {(act.rows || []).map((r, i) => (
                    <div key={i} style={{ fontSize: 11, padding: '3px 0', borderBottom: `1px solid ${T.border}`, color: r.status === 'error' ? '#ff6b6b' : r.status === 'warn' ? '#ff9500' : T.textDim }}>
                      <span style={{ fontFamily: 'monospace' }}>{fmt(r.at)}</span>
                      {' '}{r.kind === 'person' ? `👤 ${r.who || 'someone'}: ${r.text}` : `⚙ ${r.game ? `[${r.game}] ` : ''}${r.text}`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── WhatsApp integration (DoctaDirect handoff §3 + §8.1) ───────────────── */
function WhatsAppCard({ T }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [checks, setChecks] = useState(null);
  const [busy, setBusy] = useState(false);
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 16 };
  const input = { width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 8, border: `1px solid ${T.border}`, background: 'transparent', color: T.text };

  const load = async () => {
    try { const { data } = await api.get('/admin/integrations/whatsapp'); setD(data); }
    catch (e) { setD({ error: e.response?.data?.error || 'Could not load' }); }
  };
  useEffect(() => { load(); }, []);
  if (!d) return <div style={card}>Loading…</div>;
  if (d.error) return <div style={card}><span style={{ color: '#ff6b6b' }}>{d.error}</span></div>;

  const FIELDS = [
    ['token', 'Access Token 🔑 (system user, never expires)'],
    ['phoneId', 'Phone Number ID (not the number itself)'],
    ['wabaId', 'WhatsApp Business Account ID (the one /phone_numbers lists the number under — NOT the asset_id in the address bar)'],
    ['display', 'Display number (as parents see it)'],
    ['verify', 'Webhook Verify Token 🔑'],
    ['secret', 'App Secret 🔑 (signs inbound webhooks)'],
    ['template', 'Template name'], ['lang', 'Template language'],
  ];
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put('/admin/integrations/whatsapp', { ...form, ...extra });
      if (data.verifyToken) window.prompt('New Verify Token — copy it NOW into Meta (shown once):', data.verifyToken);
      toast.success(`Saved: ${data.saved.join(', ')}`); setForm({}); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };

  return (
    <div style={card} data-whatsapp-admin>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <MessageCircle size={16} color={T.ice} />
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Meta WhatsApp Cloud API</h3>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: d.enabled ? '#39d98a' : '#ff9500' }}>{d.enabled ? 'CONFIGURED' : 'NOT READY YET'}</span>
      </div>
      <div style={{ fontSize: 12, color: T.textDim, marginBottom: 8 }}>
        Callback URL (Meta → Configuration → Webhooks, subscribe to the <strong>messages</strong> field; the Verify Token must match):
        <code style={{ marginLeft: 6, color: T.text }}>{d.callbackUrl}</code>
        <button type="button" onClick={() => { navigator.clipboard.writeText(d.callbackUrl).then(() => toast.success('Copied')); }}
          style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 6, cursor: 'pointer', fontSize: 11, background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}><Copy size={10} /> Copy</button>
      </div>
      {d.missing?.length > 0 && (
        <div style={{ fontSize: 12, color: '#ff9500', marginBottom: 8 }}>
          {d.missing.map((m, i) => <div key={i}>• {m}</div>)}
        </div>
      )}
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        {FIELDS.map(([k, label]) => (
          <label key={k} style={{ fontSize: 11, color: T.textDim }}>
            {label}
            <input style={{ ...input, marginTop: 3 }} value={form[k] ?? ''}
              placeholder={d.fields?.[k]?.masked || d.fields?.[k]?.value || (d.fields?.[k]?.set ? '•••• (set)' : '')}
              onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} />
          </label>
        ))}
      </div>
      <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>Blank keeps the current value — never re-type a token to change an ID. Pasted values are stripped of spaces and line breaks.</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button type="button" disabled={busy} onClick={() => save()} style={{ padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, background: T.ice, color: '#fff', border: 'none' }}>Save</button>
        <button type="button" disabled={busy} onClick={() => save({ generateVerify: true })} style={{ padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}>Generate Verify Token</button>
        <button type="button" disabled={busy} onClick={async () => {
          setBusy(true); setChecks('running');
          try { const { data } = await api.post('/admin/integrations/whatsapp/test'); setChecks(data.checks); }
          catch (e) { setChecks([{ name: 'Test', pass: false, note: e.response?.data?.error || 'failed to run' }]); }
          finally { setBusy(false); }
        }} style={{ padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, background: 'none', border: `1px solid ${T.ice}`, color: T.ice, fontWeight: 700 }}>Test connection</button>
        <button type="button" disabled={busy} onClick={async () => {
          try { await api.post('/admin/integrations/whatsapp/subscribe'); toast.success('Webhook subscribed (idempotent — safe to repeat)'); }
          catch (e) { toast.error(e.response?.data?.error || 'Could not subscribe'); }
        }} style={{ padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}>Subscribe webhook</button>
      </div>
      {checks === 'running' && <div style={{ fontSize: 12, color: T.textDim, marginTop: 8 }}>Asking Meta (read-only, sends nothing)…</div>}
      {Array.isArray(checks) && (
        <div style={{ marginTop: 8 }}>
          {checks.map((c, i) => (
            <div key={i} style={{ fontSize: 12, padding: '3px 0', color: c.pass ? '#39d98a' : '#ff6b6b', display: 'flex', gap: 6, alignItems: 'baseline' }}>
              {c.pass ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
              <span style={{ color: T.text }}>{c.name}</span>
              <span style={{ color: T.textDim }}>{c.note}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 11, color: T.textDim, marginTop: 10 }}>
        WEBHOOK DELIVERIES — accepted: {d.stats?.accepted || 0} (last {fmt(d.stats?.lastAcceptedAt) || 'never'}) ·
        rejected: {d.stats?.rejected || 0}{d.stats?.lastRejectedWhy ? ` (last: ${d.stats.lastRejectedWhy})` : ''}
      </div>
    </div>
  );
}


/* ── AI Lab: model comparison + training bundle (2026-10-23, per Gilles) ── */
function AiLabCard({ T, models, setModels, tick }) {
  const [avail, setAvail] = useState(null);
  const [runs, setRuns] = useState(null);
  // 2026-10-28 (per Gilles) — raw names by default, and the choice sticks
  // (per browser; the warning below still shows whenever names are raw)
  const [anon, setAnon] = useState(() => { try { return localStorage.getItem('pbTrainingAnon') === '1'; } catch { return false; } });
  const setAnonKeep = (v) => { setAnon(v); try { localStorage.setItem('pbTrainingAnon', v ? '1' : '0'); } catch { /* private mode */ } };
  const [scope, setScope] = useState('reviewed');
  const [dl, setDl] = useState(false);
  const [open, setOpen] = useState(null);   // run detail
  const [h2h, setH2h] = useState(true);    // only games every model finished
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 16 };
  const load = async () => {
    try { const { data } = await api.get('/admin/press-box/lab/runs'); setRuns(data); }
    catch (e) { setRuns({ error: e.response?.data?.error || 'Could not load the runs' }); }
  };
  useEffect(() => { api.get('/admin/press-box/lab/models').then(({ data }) => setAvail(data)).catch(() => setAvail({ models: [] })); }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [tick]);
  const pct = (x) => (x == null ? '—' : `${Math.round(x * 100)}%`);
  const th = { padding: '4px 8px', fontSize: 11, color: T.textDim, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: `1px solid ${T.border}` };
  const td = { padding: '5px 8px', fontSize: 12, color: T.text, textAlign: 'right', borderBottom: `1px solid ${T.border}`, fontVariantNumeric: 'tabular-nums' };
  const download = async () => {
    setDl(true);
    try {
      const res = await api.get('/admin/press-box/training/bundle', { params: { anonymize: anon ? '1' : '0', scope }, responseType: 'blob', timeout: 15 * 60000 });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `pressbox-training-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 10000);
      toast.success('Training bundle downloaded');
    } catch { toast.error('Could not build the bundle'); }
    finally { setDl(false); }
  };
  return (
    <div style={card} data-ai-lab>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>AI Lab — which model reads the narration best?</h3>
        <button type="button" onClick={load} style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}><RefreshCw size={12} /> Refresh</button>
      </div>
      <div style={{ fontSize: 12, color: T.textDim, marginBottom: 10, lineHeight: 1.5 }}>
        Pick the models, tick games in the list above, and press <strong style={{ color: '#b07cff' }}>Compare models</strong>. Each model re-reads the
        game&rsquo;s kept transcript as a <em>shadow run</em> — the game&rsquo;s own stats never change — and is scored against the stats as the narrator
        corrected them. The production pass is scored too, as the baseline. Truth is only as good as the reviews: games with corrections count most.
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: T.textDim }}>Models:</span>
        {(avail?.models || models).map(m => (
          <label key={m} style={{ fontSize: 12, color: T.text, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999,
            border: `1px solid ${models.includes(m) ? '#b07cff' : T.border}`, cursor: 'pointer' }}>
            <input type="checkbox" checked={models.includes(m)} onChange={(e) => setModels(x => (e.target.checked ? [...x, m] : x.filter(y => y !== m)).slice(0, 4))} />
            {m}{avail?.current === m ? ' (production)' : ''}
          </label>
        ))}
        {avail?.note && <span style={{ fontSize: 12, color: '#ff9500' }}>{avail.note}</span>}
      </div>

      {runs?.summary?.length > 0 && (
        <label style={{ fontSize: 12, color: T.textDim, display: 'inline-flex', gap: 5, alignItems: 'center', marginBottom: 6 }}
          title="A model that failed on a hard game would look better if that game were left out of its average">
          <input type="checkbox" checked={h2h} onChange={(e) => setH2h(e.target.checked)} />
          Same games only (every model finished them) — the fair comparison
        </label>
      )}
      {runs?.summary?.length > 0 && h2h && !(runs.headToHead || []).length && (
        <div style={{ fontSize: 12, color: T.textDim, marginBottom: 12 }}>No game has a finished run from every model yet.</div>
      )}
      {(h2h ? runs?.headToHead : runs?.summary)?.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>Model</th>
              <th style={th} title="Games scored (latest run per game)">Games</th>
              <th style={th} title="0-100: score exact 30 · goal scorers 30 · stat counts 25 · sanity 15">Score</th>
              <th style={th} title="Final score exactly right">Final ✓</th>
              <th style={th} title="Goals with the right scorer">Scorer ✓</th>
              <th style={th} title="Goal matched in time, wrong or no scorer">Scorer ✗</th>
              <th style={th} title="Real goals the model missed">Missed</th>
              <th style={th} title="Goals the model added that are not in the corrected stats (phantoms = goals the narrator deleted)">Extra (phantom)</th>
              <th style={th} title="How close shot/save/faceoff/penalty totals are">Counts</th>
              <th style={th} title="Share of player stats with no player named">Unknown</th>
              <th style={th} title="Saves by skaters, goals/faceoffs by goalies, numbers not on the roster">Sanity</th>
              <th style={th} title="Model minutes per game">Min</th>
            </tr></thead>
            <tbody>
              {(h2h ? runs.headToHead : runs.summary).map(s2 => (
                <tr key={s2.model}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: s2.kind === 'official' ? T.textDim : T.text }}>{s2.model}</td>
                  <td style={td}>{s2.games}</td>
                  <td style={{ ...td, fontWeight: 800, color: '#b07cff' }}>{s2.composite}</td>
                  <td style={td}>{s2.scoreExactRate}%</td>
                  <td style={{ ...td, color: '#39d98a' }}>{s2.scorerRight}</td>
                  <td style={td}>{s2.scorerWrong}</td>
                  <td style={td}>{s2.missed}</td>
                  <td style={td}>{s2.extra}{s2.phantoms ? ` (${s2.phantoms})` : ''}</td>
                  <td style={td}>{pct(s2.countAccuracy)}</td>
                  <td style={td}>{pct(s2.unknownRate)}</td>
                  <td style={td}>{s2.sanityIssues + s2.offRoster}</td>
                  <td style={td}>{s2.avgMinutes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {runs?.runs?.length > 0 && (
        <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['Game', 'Model', 'Status', 'Score vs truth', 'Goals ✓/✗/missed/extra', 'Composite', ''].map(h =>
                <th key={h} style={{ ...th, textAlign: 'left' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {runs.runs.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, textAlign: 'left' }}>{r.game_date} vs {r.opponent_name || 'TBA'}</td>
                  <td style={{ ...td, textAlign: 'left' }}>{r.model}{r.kind === 'official' ? ' (official)' : ''}</td>
                  <td style={{ ...td, textAlign: 'left', color: r.status === 'error' ? '#ff6b6b' : r.status === 'done' ? '#39d98a' : '#b07cff' }}
                    title={r.error || ''}>{r.status}{r.status === 'error' && r.error ? ` — ${String(r.error).slice(0, 40)}` : ''}</td>
                  <td style={{ ...td, textAlign: 'left' }}>{r.metrics ? `${r.metrics.score.model.us}–${r.metrics.score.model.them} vs ${r.metrics.score.truth.us}–${r.metrics.score.truth.them} ${r.metrics.score.exact ? '✓' : '✗'}` : '—'}</td>
                  <td style={{ ...td, textAlign: 'left' }}>{r.metrics ? `${r.metrics.goals.scorerRight}/${r.metrics.goals.scorerWrong}/${r.metrics.goals.missed}/${r.metrics.goals.extra}` : '—'}</td>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 800, color: '#b07cff' }}>{r.metrics?.composite ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'left' }}>
                    {r.status === 'done' && (
                      <button type="button" onClick={async () => {
                        try { const { data } = await api.get(`/admin/press-box/lab/runs/${r.id}`); setOpen(data); }
                        catch { toast.error('Could not load the run'); }
                      }} style={{ fontSize: 11, padding: '1px 8px', borderRadius: 8, cursor: 'pointer', background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}>Goals</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {runs?.runs?.length === 0 && <div style={{ fontSize: 12, color: T.textDim, marginBottom: 12 }}>No comparison runs yet.</div>}

      {open && (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 10, marginBottom: 12, fontSize: 12, color: T.text }}>
          <div style={{ display: 'flex', marginBottom: 6 }}>
            <strong>{open.run.model} — goals, model vs corrected</strong>
            <button type="button" onClick={() => setOpen(null)} style={{ marginLeft: 'auto', fontSize: 11, padding: '1px 8px', borderRadius: 8, cursor: 'pointer', background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}>Close</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ color: T.textDim, marginBottom: 4 }}>Model</div>
              {(open.run.result?.events || []).filter(e => e.type === 'GOAL').map((e, i) => (
                <div key={i}>{Math.floor(e.t / 60)}:{String(Math.round(e.t % 60)).padStart(2, '0')} {e.team === 'US' ? `#${e.player ?? '?'}` : 'them'} <span style={{ color: T.textDim }}>{e.said ? `“${e.said.slice(0, 60)}”` : ''}</span></div>
              ))}
            </div>
            <div>
              <div style={{ color: T.textDim, marginBottom: 4 }}>Corrected ({open.truth.quality?.tier})</div>
              {open.truth.events.filter(e => e.type === 'GOAL').map((e, i) => (
                <div key={i}>{e.t != null ? `${Math.floor(e.t / 60)}:${String(Math.round(e.t % 60)).padStart(2, '0')}` : '—'} {e.team === 'US' ? `#${e.player ?? '?'}` : 'them'}{e.corrected ? ' ✎' : ''}</div>
              ))}
              {open.truth.deleted.filter(d => d.type === 'goal').map((d, i) => (
                <div key={`d${i}`} style={{ color: '#ff6b6b', textDecoration: 'line-through' }}>{d.t != null ? `${Math.floor(d.t / 60)}:${String(Math.round(d.t % 60)).padStart(2, '0')}` : ''} deleted by the narrator</div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>Training data</div>
        <div style={{ fontSize: 12, color: T.textDim, marginBottom: 8, lineHeight: 1.5 }}>
          One zip: every game&rsquo;s transcript, what the AI produced and what it should have produced (after your fixes), the comparison runs,
          chat-format fine-tuning files built with the engine&rsquo;s exact prompt (train/eval split by game), a README and starter scripts for
          Gemma 4 or Qwen. Send the <code>dataset/</code> and <code>comparisons/</code> folders to Claude for error analysis.
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: T.text, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
            <input type="checkbox" checked={anon} onChange={(e) => setAnonKeep(e.target.checked)} /> Replace players&rsquo; names
          </label>
          <select value={scope} onChange={(e) => setScope(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', borderRadius: 8, background: 'transparent', color: T.text, border: `1px solid ${T.border}` }}>
            <option value="reviewed">Reviewed / published games only</option>
            <option value="all">All games (incl. unreviewed)</option>
          </select>
          <button type="button" disabled={dl} onClick={download}
            style={{ padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, background: '#b07cff', color: '#fff', border: 'none' }}>
            {dl ? 'Building… (a minute or two)' : 'Download training bundle'}
          </button>
        </div>
        {!anon && <div style={{ fontSize: 11, color: '#ff9500', marginTop: 6 }}>Raw names: these are children&rsquo;s names — keep the file on machines you control.</div>}
      </div>
    </div>
  );
}


/* ── Video analysis toggles (2026-10-26, per Gilles) ──────────────────────
   Which analyses run on an uploaded game video. Unticked = skipped, saving
   processing time. The vision-worker analyses are listed for later; they
   run nothing until that worker is installed on meadowpark. */
// 2026-10-28 (per Gilles) — every stored game video, each with a re-run:
// today that redoes the narration sound-match (sync + game start) and the
// clips; the Phase-2 vision analyses will run from the same button.
function GameVideosCard({ T }) {
  const [rows, setRows] = useState(null);
  const [busyRow, setBusyRow] = useState(null);
  const load = () => api.get('/admin/press-box/videos').then(({ data }) => setRows(data.rows || []))
    .catch(e => setRows({ error: e.response?.data?.error || 'Could not load the videos' }));
  useEffect(() => { load(); }, []);
  const rerun = async (r) => {
    setBusyRow(r.gameId);
    try {
      await api.post(`/admin/press-box/videos/${r.gameId}/reanalyze`, {}, { timeout: 30000 });
      toast.success('Video AI started — sync + scoreboard read + clips (a few minutes; refresh to follow)');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not re-run'); }
    finally { setBusyRow(null); }
  };
  const mmssW = (ms) => ms != null ? `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}` : '—';
  return (
    <div className="card mb-4" data-game-videos-admin>
      <div className="flex items-center gap-2 font-bold mb-1">
        <Activity size={15} /> Game videos
        <span className="text-xs font-normal" style={{ color: T.textDim }}>every stored video — re-run the video AI from here</span>
        <button type="button" className="btn-ghost" style={{ padding: '4px 10px', marginLeft: 'auto' }} onClick={load}><RefreshCw size={12} /></button>
      </div>
      {!rows ? <div className="text-sm" style={{ color: T.textDim }}>Loading…</div>
        : rows.error ? <div className="text-sm" style={{ color: 'var(--penalty)' }}>{rows.error}</div>
        : rows.length === 0 ? <div className="text-sm" style={{ color: T.textDim }}>No game videos uploaded yet.</div>
        : rows.map(r => (
          <div key={r.gameId} className="text-xs py-2" style={{ borderTop: `1px solid ${T.border || 'var(--border)'}` }} data-video-admin-row>
            <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
              <strong>{r.team} · {r.game}</strong>
              <span style={{ color: T.textDim }}>{r.durationMin ? `${r.durationMin} min` : ''}{r.gb ? ` · ${r.gb} GB` : ''}{r.segments > 1 ? ` · ${r.segments} segments` : ''}</span>
              <span style={{ marginLeft: 'auto' }}>
                {r.status === 'stored' && (
                  <button type="button" className="btn-ghost" style={{ padding: '3px 8px' }} disabled={busyRow === r.gameId}
                    data-video-admin-rerun onClick={() => rerun(r)}>
                    {busyRow === r.gameId ? 'Re-running…' : 'Re-run video AI'}</button>
                )}
              </span>
            </div>
            <div style={{ color: T.textDim, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span>{r.status === 'stored' ? 'stored' : r.status}</span>
              <span>sync: {r.syncSource === 'auto' ? `auto (conf ${r.syncConfidence ?? '?'})` : r.syncSource}</span>
              <span>game start {mmssW(r.gameStartMs)}</span>
              <span>shirts {r.jerseyColor || '—'}</span>
              <span>clips {r.clipsStatus}{r.clipsNote ? ` — ${r.clipsNote}` : ''}</span>
            </div>
          </div>
        ))}
    </div>
  );
}

// 2026-10-27 (per Gilles) — every official pass: the model, which Whisper
// heard the tape (5090 / 1070 / CPU), and how long transcription and the
// analysis took. Downloadable as JSON.
const secWord = (v) => (v == null ? '—' : v >= 90 ? `${Math.round(v / 60)} min` : `${Math.round(v)} s`);
// 2026-11-03 (per Gilles: "it's hard to know what is happening") — one pass,
// opened: every step with the card that did it and how long, why each
// stretch fell back, and the plain-words explanation of "fell back".
const FELL_BACK_WHY = {
  busy: 'the GPU broker had no card for the call (another job held it)',
  timeout: 'the model did not answer within the time allowed',
  refused: 'the broker refused the key or the model',
  unreachable: 'the broker could not be reached',
  server: 'the broker answered with a server error',
  tripped: 'the pass had already stopped asking the model (see the step note)',
  unknown: 'no usable answer',
};
function PassDetail({ T, r }) {
  const d = r.detail || {};
  const steps = Array.isArray(d.steps) ? d.steps : [];
  const fb = Array.isArray(d.fallback) ? d.fallback : [];
  const mmss = (t) => `${Math.floor(t / 60)}:${String(Math.round(t % 60)).padStart(2, '0')}`;
  const isVideo = r.kind === 'video';
  return (
    <div style={{ marginTop: 6, padding: '8px 10px', borderRadius: 8, background: 'var(--surface-2, rgba(0,0,0,0.04))' }} data-pass-detail>
      {d.note && <div style={{ color: T.textDim, marginBottom: 4 }}>{d.note}</div>}
      {steps.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ color: T.textDim, textAlign: 'left' }}><th style={{ padding: '2px 6px' }}>step</th><th style={{ padding: '2px 6px' }}>card</th><th style={{ padding: '2px 6px' }}>took</th><th style={{ padding: '2px 6px' }}>what happened</th></tr></thead>
          <tbody>
            {steps.map((st, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${T.border || 'var(--border)'}` }}>
                <td style={{ padding: '3px 6px', fontWeight: 600 }}>{st.name}</td>
                <td style={{ padding: '3px 6px' }}>{st.gpu || '—'}</td>
                <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>{isVideo ? secWord((st.ms || 0) / 1000) : (st.seconds != null ? secWord(st.seconds) : '—')}</td>
                <td style={{ padding: '3px 6px', whiteSpace: 'normal' }}>{st.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {r.fallback_windows > 0 && (
        <div style={{ marginTop: 6, whiteSpace: 'normal' }}>
          <div style={{ fontWeight: 700 }}>"{r.fallback_windows} fell back" — what it means</div>
          <div style={{ color: T.textDim }}>
            Press Box AI reads the transcript in stretches of about 2½ minutes. For each stretch it asks the model on the 5090 (up to 3 tries).
            When a stretch gets no usable answer, that stretch keeps the RULES' reading (the keyword pass — shots, goals, saves by what was said) instead of the model's.
            The stats are still complete; those minutes were just not checked by the AI. The automatic looks (up to 3) ask only for the missing stretches again; a hand re-run tries them all.
          </div>
          {fb.length > 0 ? (
            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
              {fb.map((f, i) => (
                <li key={i}><strong>{mmss(f.t)}</strong> on the tape — {f.why || FELL_BACK_WHY[f.kind] || FELL_BACK_WHY.unknown}{f.attempts ? ` (${f.attempts} tr${f.attempts === 1 ? 'y' : 'ies'})` : ''}{f.error && f.error !== f.why ? <span style={{ color: T.textDim }}> · {f.error}</span> : null}</li>
              ))}
            </ul>
          ) : <div style={{ color: T.textDim, marginTop: 2 }}>This pass ran on an engine that did not yet record the reason per stretch (engine 2.14.6 does).</div>}
        </div>
      )}
      {d.queue && <div style={{ color: T.textDim, marginTop: 4 }}>Queue: {typeof d.queue === 'string' ? d.queue : JSON.stringify(d.queue)}</div>}
      {r.error && <div style={{ color: 'var(--penalty)', marginTop: 4, whiteSpace: 'normal' }}>{r.error}</div>}
      {r.why_gpu && <div style={{ color: T.textDim, marginTop: 4, whiteSpace: 'normal' }}>Why not the 5090: {r.why_gpu}</div>}
      <div style={{ color: T.textDim, marginTop: 4 }}>
        {r.started_at ? `started ${new Date(r.started_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' })} · ` : ''}
        logged {new Date(r.at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' })}
        {r.recording_id ? ` · recording ${String(r.recording_id).slice(0, 8)}` : ''}
      </div>
    </div>
  );
}

function PassLogCard({ T, labModels = [] }) {
  const [rows, setRows] = useState(null);
  const [cmp, setCmp] = useState(null);     // transcript comparison runs
  const [busyRow, setBusyRow] = useState(null);
  // 2026-11-02 (per Gilles: "only the video AI is showing") — Audio / Video /
  // All; the video runs' several rows each were crowding the audio out
  const [kind, setKind] = useState('all');
  const [openRow, setOpenRow] = useState(null);
  const load = (k = kind) => {
    api.get('/admin/press-box/pass-log', { params: { limit: 100, kind: k } })
      .then(({ data }) => setRows(data.rows || []))
      .catch(e => setRows({ error: e.response?.data?.error || 'Could not load the pass log' }));
    api.get('/admin/press-box/transcript-compare').then(({ data }) => setCmp(data.rows || [])).catch(() => setCmp([]));
  };
  useEffect(() => { load(kind); }, [kind]);   // eslint-disable-line react-hooks/exhaustive-deps
  // 2026-10-27 (per Gilles) — re-run the transcript on the 5090 ONLY and have
  // the model judge old vs new; and re-run the ANALYSIS with the AI Lab's
  // models on this one game.
  const compare5090 = async (r) => {
    if (!window.confirm(`Decode this recording again on the 5090 ONLY (it waits for the card), then have the stats model compare the old (${r.heard_on}) and new transcripts?\n\nThe game's stats are re-run from the new transcript.`)) return;
    setBusyRow(r.id);
    try {
      await api.post(`/admin/press-box/pass-log/${r.recording_id}/compare-5090`, {});
      toast.success('Comparison started — the verdict lands under the Pass log when the pass finishes');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not start the comparison'); }
    finally { setBusyRow(null); }
  };
  const compareModels = async (r) => {
    if (!labModels.length) { toast.error('Pick at least one model in the AI Lab card above'); return; }
    if (!window.confirm(`Shadow-run ${labModels.join(' vs ')} on this game's kept transcript? The game's stats are NOT changed; results land in the AI Lab card.`)) return;
    setBusyRow(r.id);
    try {
      const { data } = await api.post('/admin/press-box/lab/compare', { items: [{ teamId: r.team_id, gameId: r.game_id }], models: labModels });
      toast.success(`${data.queued} run(s) queued — see the AI Lab card`);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not start the model comparison'); }
    finally { setBusyRow(null); }
  };
  const download = async () => {
    try {
      const { data } = await api.get('/admin/press-box/pass-log', { params: { limit: 1000, download: 1 }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(data);
      a.download = `press-box-pass-log-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    } catch { toast.error('Could not download the log'); }
  };
  const HEARD = { '5090': 'Whisper 5090', '1070': 'Whisper 1070', '5090+1070': 'Whisper 5090 + 1070', gpu: 'Whisper (GPU)', cpu: 'CPU', reused: 'reused transcript' };
  return (
    <div className="card mb-4" data-pass-log>
      <div className="flex items-center gap-2 font-bold mb-1">
        <Activity size={15} /> Pass log
        <span className="text-xs font-normal" style={{ color: T.textDim }}>every official pass: model, which Whisper heard it, times</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ display: 'inline-flex', gap: 2 }} data-pass-log-filter>
            {[['all', 'All'], ['audio', 'Audio'], ['video', 'Video'], ['transcribe', 'Transcriptions']].map(([k, label]) => (
              <button key={k} type="button" className="btn-ghost" style={{ padding: '3px 8px', fontWeight: kind === k ? 700 : 400, color: kind === k ? 'var(--ice)' : undefined }}
                onClick={() => setKind(k)} data-pass-log-kind={k}>{label}</button>
            ))}
          </span>
          <button type="button" className="btn-ghost" style={{ padding: '4px 10px' }} onClick={() => load()} title="Refresh"><RefreshCw size={12} /></button>
          <button type="button" className="btn-ghost" style={{ padding: '4px 10px' }} onClick={download} data-pass-log-download><FileText size={12} /> Download JSON</button>
        </span>
      </div>
      {!rows ? <div className="text-sm" style={{ color: T.textDim }}>Loading…</div>
        : rows.error ? <div className="text-sm" style={{ color: 'var(--penalty)' }}>{rows.error}</div>
        : rows.length === 0 ? <div className="text-sm" style={{ color: T.textDim }}>{kind === 'all' ? 'No passes logged yet — the log starts with the next analysis.' : `No ${kind} passes in the log yet.`}</div>
        : (
        <div>
          {/* 2026-10-27 (per Gilles) — no sideways scrolling: each pass is a
              stacked row that wraps on any screen. */}
          {rows.map(r => {
            const isVideo = r.kind === 'video';
            const isTranscribe = r.kind === 'transcribe';
            const tag = isVideo ? ['video AI', '#b069ff'] : isTranscribe ? ['transcription', 'var(--puck)'] : ['audio AI', 'var(--ice)'];
            const stateWord = r.status === 'final' ? (isVideo ? 'finished' : isTranscribe ? 'decoded' : 'applied')
              : r.status === 'started' ? (isVideo ? 'started' : 're-run requested')
              : r.status === 'queued' ? 'in line' : r.status === 'skipped' ? 'skipped' : 'failed';
            const stateColor = r.status === 'final' ? 'var(--goal)' : ['started', 'queued'].includes(r.status) ? 'var(--ice)' : r.status === 'skipped' ? 'var(--puck)' : 'var(--penalty)';
            const isOpen = openRow === r.id;
            return (
            <div key={r.id} className="text-xs py-2" style={{ borderTop: `1px solid ${T.border || 'var(--border)'}` }} data-pass-row={isVideo ? 'video' : isTranscribe ? 'transcribe' : 'audio'}>
              {/* 2026-11-03 (per Gilles) — the row opens: the steps, which card
                  did each, how long, what went wrong, and what "fell back" means */}
              <div className="flex items-center gap-2" style={{ flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => setOpenRow(isOpen ? null : r.id)} data-pass-row-toggle>
                <span style={{ color: T.textDim, width: 10 }}>{isOpen ? '▾' : '▸'}</span>
                <strong>{new Date(r.at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</strong>
                <span>{r.team || '?'}{r.opponent ? ` · ${r.game_date || ''} vs ${r.opponent}` : ''}</span>
                <span style={{ color: tag[1], fontWeight: 700 }}>{tag[0]}</span>
                <span style={{ color: stateColor, fontWeight: 700 }} title={r.error || ''}>
                  {stateWord}{r.fallback_windows ? ` · ${r.fallback_windows} fell back` : ''}
                </span>
                {r.status === 'skipped' && <span style={{ color: T.textDim }}>{r.error}</span>}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                  {!isVideo && !isTranscribe && r.status === 'final' && r.recording_id && ['1070', 'cpu', '5090+1070'].includes(r.heard_on) && (
                    <button type="button" className="btn-ghost" style={{ padding: '3px 8px' }} disabled={busyRow === r.id}
                      title="Decode again on the 5090 only, then the model judges old vs new" data-compare-5090
                      onClick={() => compare5090(r)}>Re-run on 5090</button>
                  )}
                  {!isVideo && !isTranscribe && r.status === 'final' && r.game_id && (
                    <button type="button" className="btn-ghost" style={{ padding: '3px 8px' }} disabled={busyRow === r.id}
                      title="Shadow-run the AI Lab's models on this game (stats unchanged)" data-compare-models
                      onClick={() => compareModels(r)}>Compare models</button>
                  )}
                </span>
              </div>
              {isVideo ? (
                <div style={{ color: T.textDim, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span>{r.model || '—'}</span>
                  {r.status === 'started'
                    ? <span>sound-sync → scoreboard → goal check (GPU) → clips; the finished row follows</span>
                    : r.status === 'skipped' ? null
                    : (Array.isArray(r.detail?.steps) ? r.detail.steps : []).map((st, i) =>
                        <span key={i}>{st.name} {secWord(st.ms / 1000)}{st.note ? ` (${st.note})` : ''}</span>)}
                  {r.status === 'final' && <span>total {secWord(r.total_seconds)}</span>}
                </div>
              ) : isTranscribe ? (
                <div style={{ color: T.textDim, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: r.heard_on === 'cpu' ? 'var(--puck)' : 'var(--goal)' }}>{HEARD[r.heard_on] || r.heard_on || '—'}</span>
                  <span>{r.detail?.steps?.[0]?.gpu || ''}</span>
                  <span>decode {secWord(r.transcribe_seconds)}{r.tape_seconds ? ` for ${Math.round(r.tape_seconds / 60)} min of tape` : ''}</span>
                </div>
              ) : r.status === 'started'
                ? <div style={{ color: T.textDim, marginTop: 2 }}>the click — the applied/failed row follows when the pass ends{r.mode ? ` (${r.mode})` : ''}</div>
                : r.status === 'skipped' ? null
                : <div style={{ color: T.textDim, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span>{r.model || (r.mode === 'rules' ? 'rules only' : '—')}</span>
                <span style={{ fontWeight: 600, color: r.heard_on === 'cpu' ? 'var(--puck)' : 'var(--goal)' }}>{HEARD[r.heard_on] || r.heard_on || '—'}</span>
                <span>transcribe {r.transcribe_seconds == null && r.heard_on === 'reused' ? 'reused' : secWord(r.transcribe_seconds)}</span>
                <span>analyse {secWord(r.analyze_seconds)}</span>
                <span>total {secWord(r.total_seconds)}</span>
                {Number.isInteger(r.windows) && r.windows > 0 && (
                  <span title="stretches of the transcript the model read / model calls made / answers reused from the last run">
                    {r.windows} stretch{r.windows === 1 ? '' : 'es'}{Number.isInteger(r.calls) ? `, ${r.calls} call${r.calls === 1 ? '' : 's'}` : ''}{r.cached ? `, ${r.cached} reused` : ''}
                  </span>
                )}
                {r.queued_seconds >= 30 && <span style={{ color: 'var(--puck)' }}>in line for the card {secWord(r.queued_seconds)}</span>}
              </div>}
              {r.why_gpu && r.heard_on !== '5090' && !isOpen && (
                <div style={{ color: T.textDim, marginTop: 2, whiteSpace: 'normal' }}>Why not the 5090: {r.why_gpu}</div>
              )}
              {isOpen && <PassDetail T={T} r={r} />}
            </div>
            );
          })}
        </div>
      )}
      {Array.isArray(cmp) && cmp.length > 0 && (
        <div className="mt-3">
          <div className="font-bold text-sm mb-1">Transcript comparisons <span className="text-xs font-normal" style={{ color: T.textDim }}>old vs 5090 re-run, judged by the stats model</span>
            <button type="button" className="btn-ghost" style={{ padding: '2px 8px', marginLeft: 8 }} data-compare-download
              onClick={async () => {
                try {
                  const { data } = await api.get('/admin/press-box/transcript-compare', { params: { download: 1 }, responseType: 'blob' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(data);
                  a.download = `transcript-comparisons-${new Date().toISOString().slice(0, 10)}.json`;
                  a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 10000);
                } catch { toast.error('Could not download'); }
              }}><FileText size={11} /> JSON</button>
          </div>
          {cmp.map(c => (
            <div key={c.id} className="text-xs py-1.5" style={{ borderTop: `1px solid ${T.border || 'var(--border)'}` }} data-compare-row>
              <div>
                <strong>{c.team || '?'}{c.opponent ? ` · ${c.game_date || ''} vs ${c.opponent}` : ''}</strong>
                {' — '}{c.a_label} vs {c.b_label}{' · '}
                {c.status === 'running' ? <span style={{ color: 'var(--ice)' }}>running…</span>
                  : c.status === 'failed' ? <span style={{ color: 'var(--penalty)' }}>failed — {c.error}</span>
                  : <strong style={{ color: c.better === 'same' ? T.textDim : 'var(--goal)' }}>{c.better === 'same' ? 'effectively the same' : `${c.better} heard better`}</strong>}
                {c.diff?.similarity != null ? <span style={{ color: T.textDim }}> · {Math.round(c.diff.similarity * 100)}% identical words</span> : null}
              </div>
              {c.status === 'done' && c.verdict ? <div style={{ color: T.textDim, marginTop: 2 }}>{c.verdict}</div> : null}
              {c.status === 'done' && Array.isArray(c.diff?.differences) && c.diff.differences.length > 0 && (
                <details><summary style={{ cursor: 'pointer' }}>{c.diff.differences.length} difference(s)</summary>
                  <ul style={{ margin: '4px 0 0 16px' }}>{c.diff.differences.map((d, i) => <li key={i}>{d}</li>)}</ul></details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VideoAnalysisCard({ T }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get('/admin/press-box/video-settings').then(({ data }) => setS(data))
    .catch(e => setS({ error: e.response?.data?.error || 'Could not load the video settings' }));
  useEffect(() => { load(); }, []);
  const LABELS = {
    goal_clips: ['Goal clips', 'a clip per goal, cut from the video at the narration’s goal time'],
    goal_against_clips: ['Goals-against clips', 'the same for goals against (goalie review)'],
    reel: ['Highlight reel', 'our goals joined into one video'],
    possession_zones: ['Puck possession by zone', 'offensive / neutral / defensive zone time, read during Re-run video AI'],
    on_ice_at_goals: ['Who was on the ice at each goal', 'reads our jersey numbers around the goal'],
    shift_times: ['Shift times per skater', 'how long each shift ran'],
    heatmaps: ['Skating heatmaps', 'where each player skated'],
  };
  const toggle = async (k, v) => {
    setSaving(true);
    try { const { data } = await api.put('/admin/press-box/video-settings', { analyses: { [k]: v } }); setS(data); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setSaving(false); }
  };
  if (!s) return null;
  if (s.error) return null;
  const row = (k) => (
    <label key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: T.text, padding: '6px 0', cursor: 'pointer',
      borderBottom: `1px solid ${T.border}`, opacity: s.available[k] ? 1 : 0.75 }}>
      <input type="checkbox" checked={!!s.analyses[k]} disabled={saving} onChange={e => toggle(k, e.target.checked)} style={{ marginTop: 3 }} />
      <span>
        <strong>{LABELS[k][0]}</strong>
        {!s.available[k] && <span style={{ marginLeft: 6, fontSize: 11, color: '#ff9500', fontWeight: 700 }}>needs the vision worker on meadowpark — built, not connected (vision-worker/README.md)</span>}
        <span style={{ display: 'block', fontSize: 12, color: T.textDim }}>{LABELS[k][1]}</span>
      </span>
    </label>
  );
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }} data-video-analysis>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>Video analysis — what runs on an uploaded game video</h3>
      <div style={{ fontSize: 12, color: T.textDim, marginBottom: 8, lineHeight: 1.5 }}>
        Unticked analyses are skipped, saving processing time. The clip cutting runs on meadowpark&rsquo;s CPU; the
        goal check and zone reading run on the GPU through the broker. The tracker analyses below (jersey numbers,
        shifts, heatmaps) wait for the Phase-2 vision worker &mdash; ticking them now only remembers the choice.
      </div>
      {Object.keys(LABELS).filter(k => s.available[k]).map(row)}
      {/* 2026-10-29 (per Gilles: "the game can change a lot in 30 seconds") */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text, padding: '6px 0', borderBottom: `1px solid ${T.border}` }} data-zone-step>
        <span><strong>Zone read — a frame every</strong></span>
        <input type="number" min={5} max={60} step={1} defaultValue={s.zoneStepS ?? 5} disabled={saving}
          style={{ width: 64, padding: '4px 8px', borderRadius: 8, border: `1px solid ${T.border}`, background: 'transparent', color: T.text }}
          onBlur={async (e) => {
            const n = parseInt(e.target.value, 10);
            if (!Number.isInteger(n) || n < 5 || n > 60) { toast.error('5 to 60 seconds'); e.target.value = s.zoneStepS ?? 5; return; }
            if (n === s.zoneStepS) return;
            setSaving(true);
            try { const { data } = await api.put('/admin/press-box/video-settings', { zoneStepS: n }); setS(data); toast.success('Saved'); }
            catch (err) { toast.error(err.response?.data?.error || 'Could not save'); }
            finally { setSaving(false); }
          }} />
        <span>seconds <span style={{ color: T.textDim, fontSize: 12 }}>(5 = sharpest; smaller steps cost more GPU time per re-run)</span></span>
      </label>
      {/* 2026-10-30 (per Gilles) — the tracker on the 5090 first (meadowpark2), the 1070s otherwise */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: T.text, padding: '6px 0', cursor: 'pointer', borderBottom: `1px solid ${T.border}` }} data-tracker-prefer-5090>
        <input type="checkbox" checked={s.trackerPrefer5090 !== false} disabled={saving} style={{ marginTop: 3 }}
          onChange={async (e) => {
            setSaving(true);
            try { const { data } = await api.put('/admin/press-box/video-settings', { trackerPrefer5090: e.target.checked }); setS(data); }
            catch (err) { toast.error(err.response?.data?.error || 'Could not save'); }
            finally { setSaving(false); }
          }} />
        <span>
          <strong>Tracker on the RTX 5090 first</strong>
          <span style={{ display: 'block', fontSize: 12, color: T.textDim }}>
            meadowpark2&rsquo;s 5090 takes the tracker when its GPU broker hands over the card; if it is busy (the clinic trains 19:00&ndash;07:00) or unreachable, the two GTX 1070s on meadowpark do it. Off = the 1070s first. Each run says which card did the work.
          </span>
        </span>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text, padding: '6px 0', borderBottom: `1px solid ${T.border}` }} data-close-look>
        <span><strong>Close look around each goal &mdash;</strong></span>
        <input type="number" min={10} max={180} step={5} defaultValue={s.closeLookS ?? 60} disabled={saving}
          style={{ width: 64, padding: '4px 8px', borderRadius: 8, border: `1px solid ${T.border}`, background: 'transparent', color: T.text }}
          onBlur={async (e) => {
            const n = parseInt(e.target.value, 10);
            if (!Number.isInteger(n) || n < 10 || n > 180) { toast.error('10 to 180 seconds'); e.target.value = s.closeLookS ?? 60; return; }
            if (n === s.closeLookS) return;
            setSaving(true);
            try { const { data } = await api.put('/admin/press-box/video-settings', { closeLookS: n }); setS(data); toast.success('Saved'); }
            catch (err) { toast.error(err.response?.data?.error || 'Could not save'); }
            finally { setSaving(false); }
          }} />
        <span>seconds each side <span style={{ color: T.textDim, fontSize: 12 }}>(a second pass at 10 frames/s reading every back; numbers still unread go to the vision model on the 5090)</span></span>
      </label>
      {Object.keys(LABELS).some(k => !s.available[k]) && (
        <div style={{ fontSize: 11, color: T.textDim, margin: '10px 0 2px', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700 }}>The Phase-2 tracker (reads jersey numbers) — connect the vision worker to light these up</div>
      )}
      {Object.keys(LABELS).filter(k => !s.available[k]).map(row)}
    </div>
  );
}
